import { Button } from '@/components/ui/button'
import { ListRecordLayout } from '@/components/ListRecordLayout/ListRecordLayout'
import { RecordPanel } from '@/components/ListRecordLayout/RecordPanel'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useSettingsPanelState } from '@/hooks/useSettingsPanelState'
import { LayoutGrid, Plus, Table2, Link as LinkIcon } from 'lucide-react'
import { useState } from 'react'

interface ConnectedAccount {
	id: string
	provider: 'Google'
	email?: string
	connectedAt?: string
}

function ConnectedAccountsTable({
	accounts,
	onSelect,
	onAdd,
	selectedId,
}: {
	accounts: ConnectedAccount[]
	onSelect: (a: ConnectedAccount) => void
	onAdd: () => void
	selectedId?: string
}) {
    return (
        <Table>
            <TableHeader className="border-t border-t-border border-b border-b-border [&_tr]:!border-0">
                <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Connected</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody className="[&_tr:last-child]:!border-b [&_tr]:!border-b-border">
                {accounts.length === 0 ? (
                            <TableRow className="!border-b !border-b-border">
                        <TableCell colSpan={3} className="h-32 p-0">
                            <div className="flex flex-col items-center justify-center text-center space-y-3 bg-muted/50 h-full py-8">
                                <LinkIcon className="h-8 w-8 text-foreground/60" />
                                <p className="text-sm font-medium text-muted-foreground">No linked accounts yet</p>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" onClick={onAdd}>
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/archive/c/c1/20230822192910%21Google_%22G%22_logo.svg" alt="Google" className="h-4 w-4 mr-2" />
                                        Add Google
                                    </Button>
                                    <Button variant="outline" onClick={onAdd}>
                                        <img src="/brands/facebook.svg" alt="Facebook" className="h-4 w-4 mr-2" />
                                        Add Facebook
                                    </Button>
                                </div>
                            </div>
                        </TableCell>
                    </TableRow>
                ) : (
                    accounts.map((a) => (
                        <TableRow
                            key={a.id}
                            className={`group cursor-pointer hover:bg-muted/50 !border-b !border-b-border ${
                                a.id === selectedId ? 'bg-muted' : ''
                            }`}
                            onClick={() => onSelect(a)}
                        >
                            <TableCell>{a.provider}</TableCell>
                            <TableCell>{a.email ?? '-'}</TableCell>
                            <TableCell>{a.connectedAt ?? '-'}</TableCell>
                        </TableRow>
                    ))
                )}
            </TableBody>
        </Table>
    )
}

function ConnectedAccountsCards({
	accounts,
	onSelect,
	onAdd,
	selectedId,
}: {
	accounts: ConnectedAccount[]
	onSelect: (a: ConnectedAccount) => void
	onAdd: () => void
	selectedId?: string
}) {
	if (accounts.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center text-center space-y-3 bg-muted/50 h-40 py-8">
				<div className="h-8 w-8 shrink-0 flex items-center justify-center">
					<LinkIcon className="h-8 w-8 text-foreground/60" />
				</div>
				<p className="text-sm font-medium text-muted-foreground">No linked accounts yet</p>
				<div className="flex items-center gap-2">
                    <Button variant="outline" onClick={onAdd}>
						<img src="https://upload.wikimedia.org/wikipedia/commons/archive/c/c1/20230822192910%21Google_%22G%22_logo.svg" alt="Google" className="h-4 w-4 mr-2" />
						Add Google
					</Button>
                    <Button variant="outline" onClick={onAdd}>
						<img src="/brands/facebook.svg" alt="Facebook" className="h-4 w-4 mr-2" />
						Add Facebook
					</Button>
				</div>
			</div>
		)
	}
	return (
		<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
			{accounts.map((a) => (
				<div key={a.id} className={`border border-[#3A2E2F]/20 rounded-md p-4 cursor-pointer ${a.id === selectedId ? 'ring-2 ring-primary' : ''}`} onClick={() => onSelect(a)}>
					<div className="font-medium">{a.provider}</div>
					<div className="text-sm text-muted-foreground">{a.email ?? '-'}</div>
				</div>
			))}
		</div>
	)
}

function ConnectedAccountRecordPanel({ open, onClose, account, isNew = false }: { open: boolean; onClose: () => void; account?: ConnectedAccount; isNew?: boolean }) {
	return (
		<RecordPanel
			open={open}
			onClose={onClose}
			title={isNew ? 'Connect Account' : 'Account Details'}
		>
			{isNew ? (
				<div className="space-y-4">
					<p className="text-sm text-muted-foreground">
						Connect your Google or Facebook account to sync data across platforms.
					</p>
					<Button onClick={onClose}>Start Google Connect</Button>
				</div>
			) : (
				<div className="space-y-4">
					<div>
						<strong>Provider:</strong> {account?.provider}
					</div>
					<div>
						<strong>Email:</strong> {account?.email ?? '-'}
					</div>
				</div>
			)}
		</RecordPanel>
	)
}

export function LinkedAccountsList() {
	const { state, openCreate, openRecord, closePanel, setView } = useSettingsPanelState('linked-accounts')
	// Temporary local state until wired to API
	const [accounts] = useState<ConnectedAccount[]>([])

	const selected = state.recordId ? accounts.find((a) => a.id === state.recordId) : undefined

	const handleAdd = () => openCreate()
	const handleSelect = (a: ConnectedAccount) => openRecord(a.id)

    const listPanel = (
        <div className="flex flex-col h-full">
            {/* Header with title and controls in one line (match Users/Locations/Data Sources) */}
            <div className="flex items-center justify-between mb-4">
                <div className="text-lg font-semibold">Linked Accounts</div>
                <div className="flex items-center gap-3">
                    <div className="flex border rounded-md">
                        <Button variant={state.view === 'table' ? 'default' : 'ghost'} onClick={() => setView('table')} className="rounded-r-none border-r">
                            <Table2 className="h-4 w-4" />
                        </Button>
                        <Button variant={state.view === 'cards' ? 'default' : 'ghost'} onClick={() => setView('cards')} className="rounded-l-none">
                            <LayoutGrid className="h-4 w-4" />
                        </Button>
                    </div>
                    <Button onClick={handleAdd} className="cursor-pointer">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Account
                    </Button>
                </div>
			</div>
			<div className="flex-1 overflow-auto">
				{state.view === 'table' ? (
					<ConnectedAccountsTable accounts={accounts} onSelect={handleSelect} onAdd={handleAdd} selectedId={state.recordId || undefined} />
				) : (
					<ConnectedAccountsCards accounts={accounts} onSelect={handleSelect} onAdd={handleAdd} selectedId={state.recordId || undefined} />
				)}
			</div>
		</div>
	)

	const recordPanel = (
		<ConnectedAccountRecordPanel open={state.isOpen} onClose={closePanel} account={selected} isNew={state.mode === 'new'} />
	)

	return <ListRecordLayout listPanel={listPanel} recordPanel={recordPanel} />
}
